import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";

import { StyledAnchor } from "@/components/common/styles";
import { openSalesInquiry } from "@/components/marketing/SalesInquiryDialog";
import {
  ACCENT_DEEP,
  ACCENT_TINT,
  BUTTON_GLOW,
  BUTTON_GLOW_HOVER,
  marketingPalette,
} from "@/components/marketing/tokens";
import { BOOK_CALL_LABEL, LOG_IN_LINK, SIGN_UP_LINK } from "./navigation";

type ActionVariant = "bar" | "drawer";

/** Book a Call and Log In differ only in padding and in their resting fill. */
const QUIET = {
  position: "static",
  display: "flex",
  // `ButtonBase` leaves the UA's own button font in place.
  fontFamily: "inherit",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "8px",
  fontSize: 14,
  lineHeight: "20px",
  transition: "all 0.05s cubic-bezier(0.55, 0.055, 0.675, 0.19)",
  "&:hover": { color: ACCENT_DEEP, backgroundColor: ACCENT_TINT },
} as const;

/** The drawer's controls span its width and the quiet two rest on the tint. */
const FILL = {
  bar: { backgroundColor: "background.default" },
  drawer: { width: 1, backgroundColor: ACCENT_TINT },
};

/** A row in the bar; a stack in the drawer. */
const CLUSTER = {
  bar: {},
  drawer: {
    mt: "32px",
    pb: 5,
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "flex-start",
    rowGap: "16px",
  },
};
const BOOK = {
  bar: { p: "6.4px 12.8px" },
  drawer: { p: "6.4px 32px", height: 40 },
};
const SIGN_UP_PAD = { bar: { p: "10.08px 20px" }, drawer: { p: "16px 20px" } };
const SIGN_UP_WIDTH = { bar: {}, drawer: { width: 1 } };

/** The one control that carries the accent, in the bar and in the drawer. */
export function signUpSx(variant: ActionVariant) {
  return {
    ...SIGN_UP_WIDTH[variant],
    display: "flex",
    fontFamily: "inherit",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "8px",
    backgroundColor: "primary.main",
    color: "common.white",
    fontSize: 14,
    fontWeight: 500,
    lineHeight: "20px",
    boxShadow: BUTTON_GLOW,
    "&:hover": {
      backgroundColor: marketingPalette.accentHover,
      boxShadow: BUTTON_GLOW_HOVER,
    },
  };
}

/** The bar's right-hand cluster, and the same three controls in the drawer. */
export default function NavActions({
  variant,
  onNavigate,
}: {
  variant: ActionVariant;
  onNavigate?: () => void;
}) {
  return (
    <Box
      sx={{
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        columnGap: "6.4px",
        ...CLUSTER[variant],
      }}
    >
      {/* The published control opens a sales form over the page from behind an
          `href="#"`, so ours is a button rather than a link to nowhere. In the
          drawer the menu closes first, as the published one does. */}
      <ButtonBase
        type="button"
        aria-haspopup="dialog"
        onClick={() => {
          onNavigate?.();
          openSalesInquiry();
        }}
        sx={{ ...QUIET, ...FILL[variant], ...BOOK[variant] }}
      >
        {BOOK_CALL_LABEL}
      </ButtonBase>
      <StyledAnchor
        href={LOG_IN_LINK.href}
        onClick={onNavigate}
        sx={{
          ...QUIET,
          ...FILL[variant],
          p: "10.08px 20px",
        }}
      >
        {LOG_IN_LINK.label}
      </StyledAnchor>
      <StyledAnchor
        href={SIGN_UP_LINK.href}
        onClick={onNavigate}
        sx={{ ...signUpSx(variant), ...SIGN_UP_PAD[variant] }}
      >
        {SIGN_UP_LINK.label}
      </StyledAnchor>
    </Box>
  );
}
